import { useTranslation } from 'react-i18next';

import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import type { Deposit, DepositInput, DepositType } from './passes';

/**
 * The deposit (partial-payment) policy controls shared by the create and edit pass forms
 * (PRD #164 stories 18-20): a deposit-allowed toggle, and — only while deposits are allowed — a
 * type selector (PERCENTAGE | FIXED) and a value input. The value rides as a
 * decimal string so the scale the user typed reaches the backend `Money` VO unrounded; for a FIXED
 * amount the currency is the Venue's, applied server-side. When deposits are not allowed the
 * type/value are not sent.
 */
export interface DepositDraft {
  depositAllowed: boolean;
  depositType: DepositType;
  depositValue: string;
}

export const emptyDeposit: DepositDraft = {
  depositAllowed: false,
  depositType: 'PERCENTAGE',
  depositValue: '',
};

/** Seed the form draft from a persisted pass (edit). */
export function depositDraftFrom(deposit: Deposit): DepositDraft {
  return {
    depositAllowed: deposit.allowed,
    depositType: deposit.type ?? 'PERCENTAGE',
    depositValue: deposit.value ?? '',
  };
}

/** Map the draft back to the wire `DepositInput`; type/value are omitted when not allowed. */
export function depositInput(draft: DepositDraft): DepositInput {
  return draft.depositAllowed
    ? { allowed: true, type: draft.depositType, value: draft.depositValue }
    : { allowed: false };
}

interface DepositFieldsProps {
  draft: DepositDraft;
  currency: string;
  onChange: (patch: Partial<DepositDraft>) => void;
}

/** The deposit controls without a wrapping fieldset, so they can be embedded in another section. */
export function DepositControls({ draft, currency, onChange }: DepositFieldsProps) {
  const { t } = useTranslation('common');

  return (
    <>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          aria-label={t('products.form.depositAllowed')}
          checked={draft.depositAllowed}
          onChange={(event) => onChange({ depositAllowed: event.target.checked })}
        />
        {t('products.form.depositAllowed')}
      </label>

      {draft.depositAllowed && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="deposit-type">{t('products.form.depositType')}</Label>
            <select
              id="deposit-type"
              aria-label={t('products.form.depositType')}
              value={draft.depositType}
              onChange={(event) => onChange({ depositType: event.target.value as DepositType })}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="PERCENTAGE">{t('products.form.depositPercentage')}</option>
              <option value="FIXED">{t('products.form.depositFixed')}</option>
            </select>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="deposit-value">
              {draft.depositType === 'PERCENTAGE'
                ? t('products.form.depositValuePercent')
                : t('products.form.depositValueFixed', { currency })}
            </Label>
            <Input
              id="deposit-value"
              aria-label={t('products.form.depositValue')}
              inputMode="decimal"
              value={draft.depositValue}
              onChange={(event) => onChange({ depositValue: event.target.value })}
              required
            />
          </div>
        </div>
      )}
    </>
  );
}

/** The deposit controls in their own labelled fieldset (standalone use). */
export function DepositFields(props: DepositFieldsProps) {
  const { t } = useTranslation('common');

  return (
    <fieldset className="grid gap-3 rounded-md border p-4">
      <legend className="px-1 text-sm font-medium">{t('products.form.policy')}</legend>
      <DepositControls {...props} />
    </fieldset>
  );
}
